import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AppIcon, AppText, Box, Button, Checkbox, Column, Modal, Row, styled, useTheme, } from '@streem/ui-react';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useCompanySettings } from '../context/company_settings';
const getCustomTerms = ({ customTCUrl, customPrivacyUrl, }) => {
    const terms = [];
    if (customTCUrl) {
        terms.push({
            text: 'Terms of Use',
            url: customTCUrl,
        });
    }
    if (customPrivacyUrl) {
        terms.push({
            text: 'Privacy Policy',
            url: customPrivacyUrl,
        });
    }
    return terms;
};
export function TermsOfServiceModal({ onAccept, isLoading, isOpen, onDecline, preppingLobby, }) {
    const settings = useCompanySettings();
    const terms = getCustomTerms({
        customTCUrl: settings?.customTCUrl,
        customPrivacyUrl: settings?.customPrivacyPolicyUrl,
    });
    const [formState, setFormState] = useState({
        acceptedTos: false,
        acceptedPrivacyPolicy: false,
        acceptedCustomTerms: terms.length > 0 ? false : undefined,
    });
    const [formTouched, setFormTouched] = useState(false);
    const [formError, setFormError] = useState(false);
    const [acceptButtonDisabled, setAcceptButtonDisabled] = useState(true);
    const [displayIframe, setDisplayIframe] = useState(false);
    const [iframeSrc, setIframeSrc] = useState('');
    const [displayModal, setDisplayModal] = useState(isOpen);
    const theme = useTheme();
    // Enable the Accept button when user agrees to our policies
    useEffect(() => {
        const acceptedStreemPolicies = formState.acceptedTos && formState.acceptedPrivacyPolicy;
        const acceptedAdditionalPolicy = terms.length > 0 ? formState.acceptedCustomTerms : true;
        if (acceptedStreemPolicies && acceptedAdditionalPolicy) {
            setAcceptButtonDisabled(false);
        }
        else {
            setAcceptButtonDisabled(true);
        }
    }, [formState, settings?.customTCUrl, terms.length]);
    // Check for missing form values
    const handleCancel = useCallback(() => {
        setFormError(true);
        onDecline && onDecline();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formState, onDecline]);
    const resetFormStatus = useCallback(() => {
        !formTouched && setFormTouched(true);
        formError && setFormError(false);
    }, [formTouched, formError]);
    const showIframe = useCallback((src) => {
        setIframeSrc(src);
        setDisplayIframe(true);
        setDisplayModal(false);
    }, []);
    const hideIframe = useCallback(() => {
        setDisplayModal(true);
        setDisplayIframe(false);
        setIframeSrc('');
    }, []);
    return (_jsxs(_Fragment, { children: [_jsx(TermsConditionsPolicyIframe, { displayIframe: displayIframe, src: iframeSrc, handleClick: hideIframe }), _jsxs(Modal, { closeDelayMS: 200, hideCloseButton: true, closeOnOverlayClick: false, isOpen: displayModal, onClose: () => { }, cardProps: {
                    decoratorPosition: undefined,
                }, overlayColor: "greyA50", mobile: true, styleOverride: {
                    overlay: {
                        paddingTop: '1rem',
                        height: '100%',
                        minHeight: '-webkit-fill-available',
                        background: theme.colors.grey20,
                        zIndex: 19,
                    },
                    content: {
                        height: '100%',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                    },
                    card: {
                        borderRadius: '8px',
                    },
                }, children: [_jsx(Row, { mb: '2.5vh', children: _jsx(AppText, { "data-testid": "terms-and-conditions-header", as: "h1", size: "large", bold: true, headingFontFamily: true, children: "Terms & Conditions" }) }), _jsx(Row, { mb: '2.5vh', children: _jsx(AppText, { "data-testid": "terms-and-conditions-message", as: "p", color: "grey70", children: "We respect your privacy. Please review our Terms of Use and Privacy Policy." }) }), _jsxs(Column, { mt: '1.25vh', mb: '1vh', children: [_jsx(TermsOfServiceCheckBox, { onChange: () => {
                                    resetFormStatus();
                                    setFormState({
                                        ...formState,
                                        acceptedTos: !formState.acceptedTos,
                                        acceptedPrivacyPolicy: !formState.acceptedPrivacyPolicy,
                                    });
                                }, error: formError && !formState.acceptedTos, checked: formState.acceptedTos, testid: "accept-streem-terms-of-use-and-privacy-policy", label: "I agree to Streem's Terms of Use", children: _jsx(TermDescriptionAndLink, { description: "I agree to Streem's", firstHref: "/terms.html", linkText: "Terms of Use", secondHref: "/policy.html", secondLinkText: "Privacy Policy", onClick: showIframe }) }), terms.length > 0 && (_jsx(TermsOfServiceCheckBox, { onChange: () => {
                                    resetFormStatus();
                                    setFormState({
                                        ...formState,
                                        acceptedCustomTerms: !formState.acceptedCustomTerms,
                                    });
                                }, error: formError && !formState.acceptedCustomTerms, checked: formState.acceptedCustomTerms, testid: "accept-additional-tc", label: "I agree to additional Terms of Use", children: _jsx(CustomTermDescriptionAndLink, { description: "I agree to additional", terms: terms, onClick: showIframe }) }))] }), formError && _jsx(ErrorMessage, {}), _jsx(Row, { justifyContent: "flex-end", pt: 2, children: _jsxs(ButtonGrid, { children: [_jsx(Button, { width: "100%", px: "25px", "data-testid": 'decline-streem-policy', variant: 'secondary', onClick: handleCancel, mobile: true, children: "Decline" }), _jsx(Button, { width: "100%", mobile: true, px: "25px", "data-testid": 'accept-streem-policy', disabled: acceptButtonDisabled || preppingLobby, onClick: onAccept, loading: isLoading || preppingLobby, children: "Accept" })] }) })] })] }));
}
function TermsOfServiceCheckBox(props) {
    const theme = useTheme();
    return (_jsxs(Row, { alignItems: "flex-start", mb: '1.5vh', children: [_jsx(Box, { mr: 3, children: _jsx(Checkbox, { "data-testid": props.testid, label: props.label, checked: props.checked, onChange: props.onChange, borderColor: props.error ? theme.colors.red50 : undefined }) }), props.children] }));
}
function ErrorMessage() {
    const theme = useTheme();
    return (_jsxs(Row, { border: `1px solid ${theme.colors.red50}`, borderRadius: "3px", alignItems: "center", justifyContent: "center", px: "15px", py: "10px", mb: 4, children: [_jsx(Row, { width: "24px", height: "24px", alignItems: "center", justifyContent: "center", flexShrink: 0, mr: "15px", children: _jsx(AppIcon, { color: theme.colors.red50, name: "AlertIcon" }) }), _jsx(AppText, { "data-testid": "terms-and-conditions-error-message", children: "In order to proceed to your Streem call, you must accept terms and conditions." })] }));
}
const HighlightLink = styled.span(({ theme }) => ({
    fontWeight: 600,
    textDecoration: 'none',
    color: theme.colors.azure,
}));
const ButtonGrid = styled.div(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridColumnGap: theme.spacing.m,
    width: '100%',
    maxWidth: '375px',
}));
function TermDescriptionAndLink(props) {
    return (_jsx(Box, { ml: 4, children: _jsxs(AppText, { as: "p", size: "medium", color: "grey70", headingFontFamily: true, style: { lineHeight: '25px' }, children: [props.description + ' ', _jsx(HighlightLink, { onClick: () => props.onClick(props.firstHref), children: props.linkText }), props.secondHref && (_jsxs(_Fragment, { children: [' and ', _jsx(HighlightLink, { onClick: () => props.onClick(props.secondHref), children: props.secondLinkText })] }))] }) }));
}
function CustomTermDescriptionAndLink({ description, terms, onClick, }) {
    const numTerms = terms.length;
    return (_jsx(Box, { ml: 3, children: _jsxs(AppText, { as: "p", size: "medium", color: "grey70", style: { lineHeight: '25px' }, headingFontFamily: true, children: [description + ' ', _jsx("br", {}), _jsx(HighlightLink, { onClick: () => onClick(terms[0].url), children: terms[0].text }), numTerms > 1 && (_jsxs(Fragment, { children: [' ', "&", ' ', _jsx(HighlightLink, { style: { flexShrink: 0 }, onClick: () => onClick(terms[1].url), children: terms[1].text })] }))] }) }));
}
const TermsConditionsPolicyIframe = ({ displayIframe, src, handleClick, }) => {
    // Clicking a link in the iframe causes the parent frame to scroll
    // instead of just the iframe. This function scrolls the parent frame
    // to the top to prevent the back to call button from being hidden.
    const scrollToTop = () => {
        window.scrollTo(0, 0);
    };
    const theme = useTheme();
    return displayIframe ? (_jsxs(Box, { width: "100%", height: "100vh", position: "relative", children: [_jsxs(IframeContentWrapper, { onClick: handleClick, children: [_jsx(AppIcon, { name: "BackIcon", color: theme.colors.white }), _jsx(AppText, { semibold: true, style: { fontFamily: 'Poppins' }, color: "white", children: "Back to Call" })] }), _jsx("iframe", { width: "100%", height: "100%", src: src, title: "Terms&PolicyFrame", onLoad: () => {
                    window.addEventListener('scroll', scrollToTop);
                } })] })) : null;
};
const IframeContentWrapper = styled(Row)(({ theme }) => ({
    gap: '5px',
    backgroundColor: theme.colors.blue90,
    width: '100%',
    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
    padding: '25px 20px',
}));
//# sourceMappingURL=terms_of_service_modal.js.map